import {
  checkAssetsValidEn,
  checkAssetsValidTh,
} from '@Containers/UploadImageFormFields/utils';
import {
  ICompanyValueIncreasePlansFormValues,
  IValueUpPlanFormValues,
  IOverviewFormValues,
  IRiskFormValues,
} from '@Router/GoalAndOperationalPlans/Containers/GoalAndOperationalPlansFormProvider/types';
import { OtherExplanationsFormValues } from '@Utils/Types/FormValuesTypes';
import {
  checkEditorMessageEn,
  checkMessageEn,
  validateSpace,
  validateSpaceTextInput,
} from '@Utils/validation';
import { RiskChance } from 'src/Enum/GoalsAndOperationalPlans';

const checkRiskCompleteTh = (risks: IRiskFormValues[]): boolean => {
  if (!risks || risks.length === 0) return false;

  return risks.every((risk) => {
    const { relateRisks, chance, nameTh, characteristicTh } = risk;
    const hasNameTh = nameTh && nameTh.trim() !== '';
    const hasCharacteristicTh =
      characteristicTh && characteristicTh.trim() !== '';
    const hasRelateRisk = relateRisks && relateRisks.length > 0;
    const hasRiskChance = chance && Object.values(RiskChance).includes(chance);

    return hasNameTh && hasCharacteristicTh && hasRelateRisk && hasRiskChance;
  });
};

export const checkValueUpPlansCompleteTh = (
  valueUpPlans: IValueUpPlanFormValues[]
): boolean => {
  if (!valueUpPlans || valueUpPlans.length === 0) return false;

  return valueUpPlans.every((plan) => {
    const {
      nameTh,
      significanceTh,
      relateGoals,
      goals,
      riskManagement,
      assets,
    } = plan;
    const hasNameTh = nameTh && nameTh.trim() !== '';
    const hasSignificanceTh = significanceTh && significanceTh.trim() !== '';
    const hasRelateGoal = relateGoals && relateGoals.length > 0;
    const hasGoal = goals && goals.length > 0;

    const risks = riskManagement.risks;
    const riskIntro = riskManagement.introduction;
    const riskRemark = riskManagement.remark;

    const isRiskIntroSpaceEnValid =
      !riskIntro?.descriptionTh ||
      (riskIntro.descriptionTh.trim() !== '' &&
        validateSpace(riskIntro.descriptionTh));

    const isRiskRemarkSpaceEnValid =
      !riskRemark?.descriptionTh ||
      (riskRemark.descriptionTh.trim() !== '' &&
        validateSpaceTextInput(riskRemark.descriptionTh));

    const isRiskCompleteTh = checkRiskCompleteTh(risks);

    const isAssetsValidTh = checkAssetsValidTh({ assets });

    return (
      hasNameTh &&
      hasSignificanceTh &&
      hasRelateGoal &&
      hasGoal &&
      isRiskCompleteTh &&
      isRiskIntroSpaceEnValid &&
      isRiskRemarkSpaceEnValid &&
      isAssetsValidTh
    );
  });
};

const checkRiskCompleteEn = (risks: IRiskFormValues[]): boolean => {
  if (!risks || risks.length === 0) return false;

  return risks.every((risk) => {
    const { relateRisks, chance, nameEn, characteristicEn, measurementEn } =
      risk;
    const hasNameEn = nameEn && nameEn.trim() !== '' && checkMessageEn(nameEn);
    const hasCharacteristicEn =
      characteristicEn &&
      characteristicEn.trim() !== '' &&
      checkEditorMessageEn(characteristicEn);
    const isMeasurementEnValid =
      !measurementEn ||
      (measurementEn.trim() !== '' && checkEditorMessageEn(measurementEn));
    const hasRelateRisk = relateRisks && relateRisks.length > 0;
    const hasRiskChance = chance && Object.values(RiskChance).includes(chance);

    return (
      hasNameEn &&
      hasCharacteristicEn &&
      isMeasurementEnValid &&
      hasRelateRisk &&
      hasRiskChance
    );
  });
};

export const checkValueUpPlansCompleteEn = (
  valueUpPlans: IValueUpPlanFormValues[]
): boolean => {
  if (!valueUpPlans || valueUpPlans.length === 0) return false;

  return valueUpPlans.every((plan) => {
    const {
      nameEn,
      significanceEn,
      relateGoals,
      goals,
      riskManagement,
      assets,
    } = plan;
    const hasNameEn = nameEn && nameEn.trim() !== '' && checkMessageEn(nameEn);
    const hasSignificanceEn =
      significanceEn &&
      significanceEn.trim() !== '' &&
      checkEditorMessageEn(significanceEn);
    const hasRelateGoal = relateGoals && relateGoals.length > 0;
    const hasGoal = goals && goals.length > 0;

    const riskIntro = riskManagement.introduction;
    const riskRemark = riskManagement.remark;

    const isRiskIntroSpaceEnValid =
      !riskIntro?.descriptionEn ||
      (riskIntro.descriptionEn.trim() !== '' &&
        validateSpace(riskIntro.descriptionEn));
    const isRiskRemarkSpaceEnValid =
      !riskRemark?.descriptionEn ||
      (riskRemark.descriptionEn.trim() !== '' &&
        validateSpaceTextInput(riskRemark.descriptionEn));

    const isRiskIntroEnValid =
      !riskIntro?.descriptionEn ||
      (riskIntro.descriptionEn.trim() !== '' &&
        checkEditorMessageEn(riskIntro.descriptionEn));

    const isRiskRemarkEnValid =
      !riskRemark?.descriptionEn ||
      (riskRemark.descriptionEn.trim() !== '' &&
        checkMessageEn(riskRemark.descriptionEn));

    const risks = riskManagement.risks;
    const isRiskCompleteEn = checkRiskCompleteEn(risks);

    const isAssetsValidEn = checkAssetsValidEn({ assets });

    return (
      hasNameEn &&
      hasSignificanceEn &&
      hasRelateGoal &&
      hasGoal &&
      isRiskIntroEnValid &&
      isRiskRemarkEnValid &&
      isRiskCompleteEn &&
      isRiskIntroSpaceEnValid &&
      isRiskRemarkSpaceEnValid &&
      isAssetsValidEn
    );
  });
};

export const checkOverviewThValid = (
  overview: IOverviewFormValues
): boolean => {
  const { introduction, remark } = overview;
  const isIntroSpaceThValid =
    !introduction?.descriptionTh ||
    (introduction.descriptionTh.trim() !== '' &&
      validateSpace(introduction.descriptionTh));
  const isRemarkSpaceThValid =
    !remark?.descriptionTh ||
    (remark.descriptionTh.trim() !== '' &&
      validateSpaceTextInput(remark.descriptionTh));

  return isIntroSpaceThValid && isRemarkSpaceThValid;
};

export const checkOverviewEnValid = (
  overview: IOverviewFormValues
): boolean => {
  const { introduction, remark } = overview;
  const isIntroductionEnValid =
    !introduction?.descriptionEn ||
    (introduction.descriptionEn.trim() !== '' &&
      checkEditorMessageEn(introduction.descriptionEn));

  const isRemarkEnValid =
    !remark?.descriptionEn ||
    (remark.descriptionEn.trim() !== '' &&
      checkMessageEn(remark.descriptionEn));

  return isIntroductionEnValid && isRemarkEnValid;
};

export const checkCompanyValueIncreasePlansOverviewThValid = (
  companyValueIncreasePlans: ICompanyValueIncreasePlansFormValues
): boolean => {
  const { introduction, remark } = companyValueIncreasePlans || {};

  const isIntroSpaceThValid =
    !introduction?.descriptionTh ||
    (introduction.descriptionTh.trim() !== '' &&
      validateSpace(introduction.descriptionTh));

  const isRemarkSpaceThValid =
    !remark?.descriptionTh ||
    (remark.descriptionTh.trim() !== '' &&
      validateSpaceTextInput(remark.descriptionTh));

  return isIntroSpaceThValid && isRemarkSpaceThValid;
};

export const checkCompanyValueIncreasePlansOverviewEnValid = (
  companyValueIncreasePlans: ICompanyValueIncreasePlansFormValues
): boolean => {
  const { introduction, remark } = companyValueIncreasePlans || {};
  const isIntroductionEnValid =
    !introduction?.descriptionEn ||
    (introduction.descriptionEn.trim() !== '' &&
      checkEditorMessageEn(introduction.descriptionEn));

  const isRemarkEnValid =
    !remark?.descriptionEn ||
    (remark.descriptionEn.trim() !== '' &&
      checkMessageEn(remark.descriptionEn));

  return isIntroductionEnValid && isRemarkEnValid;
};

export const checkOtherDescriptionEnValid = (
  otherDescriptions: OtherExplanationsFormValues[]
): boolean => {
  return otherDescriptions.every((otherDescription) => {
    const { titleEn, descriptionEn } = otherDescription;

    const isTitleEnValid =
      !titleEn || (titleEn.trim() !== '' && checkMessageEn(titleEn));

    const isDescriptionEnValid =
      !descriptionEn ||
      (descriptionEn.trim() !== '' && checkEditorMessageEn(descriptionEn));

    return isTitleEnValid && isDescriptionEnValid;
  });
};
